import { PureComponent } from "react";

import { cn } from "@btnet/utilities-core";

import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { GridLineItemColumnType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import { type ILineItemColumn } from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import { LineItemColumns } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";
import { type IBaseLineItem } from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

export interface IUnitCellProps extends IPreviewLineItemProps {
    unit: string;
    className?: string;
    ellipsis?: boolean;
    previewKey?: string;
    debouncingContext?: IInputDebouncingContext;
}

export class UnitCell extends PureComponent<IGridCellRenderProps<IUnitCellProps>> {
    private handleChange = (fieldKey: KeyOfOrString<any>, value: string) => {
        const { field, onChange } = this.props;
        onChange(field.full, value);
    };

    private handleTouched = (fieldKey: KeyOfOrString<any>, touched: boolean) => {
        const { field, onBlur } = this.props;
        onBlur(field.full, touched);
    };

    render() {
        const { cellValue, isInEdit, field, canEdit, column, autoFocus, onFocus } = this.props;

        const canEditUnit = canEdit;
        const component = (
            <LineItemPreview
                fieldName="text"
                addPreviewListener={cellValue?.addPreviewListener}
                render={(preview) => (
                    <BTInput
                        className={preview ? "LineItemPreview" : undefined}
                        id={field.full}
                        data-testid={field.full}
                        value={
                            (preview?.[cellValue?.previewKey ?? ""] as string | undefined) ??
                            cellValue?.unit ??
                            undefined
                        }
                        readOnly={!isInEdit || !canEditUnit}
                        onChange={this.handleChange}
                        onBlur={this.handleTouched}
                        onInput={() => this.handleTouched(field.full, true)}
                        autoFocus={autoFocus}
                        tabIndex={canEditUnit ? 0 : undefined}
                        onFocus={onFocus}
                        disableFormSubmit
                        debouncingContext={
                            cellValue?.debouncingContext ?? {
                                useDebouncing: true,
                                inputDelay: 150,
                            }
                        }
                    />
                )}
            />
        );

        if (isInEdit) {
            return (
                <BTFormItemAutomatic
                    id={field.full}
                    className={cn(
                        cellValue?.className,
                        "LineItemCell",
                        {
                            RightAlignFormLabel: column && (column as any).align === "right",
                        },
                        { ellipsis: cellValue?.ellipsis },
                    )}
                >
                    {component}
                </BTFormItemAutomatic>
            );
        }

        return (
            <div
                className={cn(
                    "LineItemCell",
                    { lineItemEditableCell: canEditUnit },
                    { ellipsis: cellValue?.ellipsis },
                )}
                onFocus={onFocus}
            >
                {component}
            </div>
        );
    }
}

export interface IPreviewUnitCellValue extends IPreviewLineItemProps {
    value: string;
    previewKey?: string;
    ellipsis?: boolean;
    debouncingContext?: IInputDebouncingContext;
}

export const PreviewUnitCell: React.FC<IGridCellRenderProps<IPreviewUnitCellValue>> = (props) => {
    if (!props.cellValue) {
        return null;
    }
    return (
        <UnitCell
            {...props}
            cellValue={{
                unit: props.cellValue.value,
                ellipsis: props.cellValue.ellipsis ?? true,
                addPreviewListener: props.cellValue.addPreviewListener,
                previewKey: props.cellValue.previewKey,
            }}
        />
    );
};

export const getUnitCellDefaultConfig = <T extends IBaseLineItem>() =>
    ({
        id: LineItemColumns.Unit,
        name: "Unit",
        jsonKey: "unit",
        width: 100.0,
        minWidth: 48,
        ellipsis: true,
        align: "left",
        isSortable: true,
        columnType: GridLineItemColumnType.text,
        isFrozen: false,
        isHidden: false,
        Cell: UnitCell,
    }) as Partial<ILineItemColumn<T, IUnitCellProps>>;
